import {  BaseUrl } from './BaseUrlConst';
import axios from 'axios';
import { message } from 'antd';
import { getCookie, removeCookie } from '../utils/AllCookies';
import Constants from '../redux/Constants/Constants';

const ENTRY_ROUTE = '/login';
const REDIRECT_DELAY_MS = 2000;

let redirecting = false;

const handleSessionExpired = () => {
  if (redirecting || window.location.pathname.includes(ENTRY_ROUTE)) return;
  redirecting = true;

  message.error('Your session has expired. Please log in again.');
  setTimeout(() => {
    localStorage.clear();
    removeCookie(Constants.AUTH_TOKEN);
    window.location.href = ENTRY_ROUTE;
  }, REDIRECT_DELAY_MS);
};

// Remove trailing slash from BaseUrl if it exists to avoid double slashes in API endpoints
const baseUrlClean = BaseUrl?.endsWith('/') ? BaseUrl.slice(0, -1) : BaseUrl;
const API_BASE = `${baseUrlClean}/api/VMSInvoice`;

// Create axios instance with auth interceptor
const apiClient = axios.create({
  baseURL: API_BASE,
  timeout: 120000, // 2 minutes timeout
});

// Add auth token to all requests and record start time for timing logs
apiClient.interceptors.request.use(config => {
  const jwtToken = getCookie(Constants.AUTH_TOKEN);
  if (jwtToken && jwtToken.jwtToken) {
    config.headers['Authorization'] = `Bearer ${jwtToken.jwtToken}`;
  }
  config.metadata = { startTime: Date.now() };
  console.log(`[VMSInvoice] ${config.method.toUpperCase()} ${config.url} — request started`);
  return config;
}, error => {
  return Promise.reject(error);
});

// Log response timing
apiClient.interceptors.response.use(
  response => {
    const elapsed = ((Date.now() - response.config.metadata?.startTime) / 1000).toFixed(3);
    console.log(`[VMSInvoice] ${response.config.method.toUpperCase()} ${response.config.url} — request completed`);
    console.log(`[VMSInvoice] Total time: ${elapsed}s`);
    return response;
  },
  error => {
    const elapsed = ((Date.now() - error.config?.metadata?.startTime) / 1000).toFixed(3);
    console.log(`[VMSInvoice] ${error.config?.method?.toUpperCase()} ${error.config?.url} — request failed`);
    console.log(`[VMSInvoice] Total time: ${elapsed}s`);

    const status = error.response?.status;
    if (status === 401 || status === 403) {
      error.isAuthError = true;
      handleSessionExpired();
    }

    return Promise.reject(error);
  }
);

class VMSInvoiceService {
  /**
   * Upload CSV file and create batch with 'pre' status
   * @param {Object} data - Contains sageInvoices array and vendorId
   * @returns {Promise}
   */
  static uploadFile(data) {
    return apiClient.post('/upload', data, { timeout: 1800000 });
  }

  /**
   * Get all invoices for a specific batch
   * @param {number} batchId - The batch ID
   * @returns {Promise}
   */
  static getInvoicesByBatch(batchId) {
    return apiClient.get(`/batches/${batchId}/invoices`);
  }

  /**
   * Process a single invoice by ID
   * @param {number} invoiceId - The invoice detail ID
   * @param {Object} data - Contains vendorPassword and apiGuid
   * @returns {Promise}
   */
  static processInvoice(invoiceId, data) {
    return apiClient.post(`/invoices/${invoiceId}/process`, data);
  }

  /**
   * Search invoices with hierarchical structure (server-side pagination)
   * @param {Object} params - Search parameters (search, batchStatus, startDate, endDate, pageNumber, pageSize)
   * @returns {Promise}
   */
  static searchInvoices(params = {}) {
    const queryParams = new URLSearchParams();

    if (params.search) queryParams.append('search', params.search);
    if (params.batchStatus) queryParams.append('batchStatus', params.batchStatus);
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);
    if (params.pageNumber) queryParams.append('pageNumber', params.pageNumber);
    if (params.pageSize) queryParams.append('pageSize', params.pageSize);

    const queryString = queryParams.toString();
    const url = queryString ? `/search?${queryString}` : `/search`;

    return apiClient.get(url);
  }

  /**
   * Get PDF file URL for an invoice - returns the direct download URL
   * @param {string} fileName - The PDF file name (may contain full path)
   * @returns {string} - The PDF file download URL
   */
  static getPdfUrl(fileName) {
    // Build URL with filePath query parameter
    return `${API_BASE}/pdf?fileName=${encodeURIComponent(fileName)}`;
  }

  /**
   * Download PDF file by GUID with authentication
   * @param {string} fileGuid - The file GUID
   * @returns {Promise} - Blob response
   */
  static downloadPdfByGuid(fileGuid) {
    return apiClient.get('/pdf', {
      params: { fileGuide: fileGuid },
      responseType: 'blob'
    });
  }

  /**
   * Get the expected CSV column layout from the API
   * @returns {Promise<string[]>} - Array of expected header names
   */
  static async getCsvLayout() {
    const response = await apiClient.get('/csvLayout');
    return response.data;
  }

  /**
   * Parse CSV file content
   * @param {File} file - CSV file
   * @returns {Promise<Array>} - Parsed invoice data
   */
  static async parseCSV(file) {
    const validHeaders = await this.getCsvLayout();
    const expectedCount = validHeaders.length;

    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const text = e.target.result;
          const lines = text.split('\n');
          const headers = this.parseCSVLine(lines[0]);

          if (headers.length !== expectedCount) {
            return reject(new Error(`The CSV file contains ${headers.length} columns, whereas ${expectedCount} were expected.\nPlease correct the file and upload it again.`));
          }

          const missingHeaders = validHeaders.filter(h => !headers.includes(h));
          const unexpectedHeaders = headers.filter(h => !validHeaders.includes(h));

          if (missingHeaders.length > 0) {
            return reject(new Error(`The CSV file is missing the following columns: ${missingHeaders.join(', ')}.\nPlease correct the file and upload it again.`));
          }

          if (unexpectedHeaders.length > 0) {
            return reject(new Error(`The CSV file contains unexpected columns: ${unexpectedHeaders.join(', ')}.\nPlease correct the file and upload it again.`));
          }
          
          const invoices = [];
          
          for (let i = 1; i < lines.length; i++) {
            if (lines[i].trim() === '') continue;
            
            const values = this.parseCSVLine(lines[i]);
            const invoice = {};
            
            headers.forEach((header, index) => {
              const value = values[index];
              
              // Parse data types based on header name
              if (['CompanyKey', 'VendorLocationKey', 'UnitKey', 'CompanyInventoryLinkKey', 'ResidentKey'].includes(header)) {
                invoice[header] = parseInt(value) || 0;
              } else if (['Amount', 'Cost', 'Quantity'].includes(header)) {
                invoice[header] = parseFloat(value) || 0;
              } else if (['OnlineBoardApproval'].includes(header)) {
                invoice[header] = value?.toLowerCase() === 'true';
              } else {
                invoice[header] = value || '';
              }
            });
            
            // If ImageBase64 column exists (contains folder path), combine with FileName
            // ImageBase64 format: S:\Accounting\Accounts Payable\1-AAM Invoices\2020-2025\2025\AAM Invoices 12-12-25
            // Extract from "1-AAM Invoices" onward and replace \ with /
            if (invoice.ImageBase64 && invoice.FileName) {
              let folderPath = invoice.ImageBase64.replace(/\\/g, '/'); // Replace all \ with /
              const marker = '1-AAM Invoices';
              const markerIndex = folderPath.indexOf(marker);
              if (markerIndex !== -1) {
                folderPath = folderPath.substring(markerIndex); // Keep from "1-AAM Invoices" onward
              }
              folderPath = folderPath.replace(/\/$/, ''); // Remove trailing slash
              const fileName = invoice.FileName;
              invoice.FileName = `${folderPath}/${fileName}`;
            }
            
            invoices.push(invoice);
          }
          
          resolve(invoices);
        } catch (error) {
          reject(new Error('Failed to parse CSV file: ' + error.message));
        }
      };
      
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsText(file);
    });
  }

  /**
   * Parse a single CSV line handling quoted fields
   * @param {string} line - CSV line
   * @returns {Array<string>} - Array of field values
   */
  static parseCSVLine(line) {
    const result = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      const nextChar = line[i + 1];
      
      if (inQuotes) {
        if (char === '"' && nextChar === '"') {
          // Escaped quote inside quoted field
          current += '"';
          i++; // Skip next quote
        } else if (char === '"') {
          // End of quoted field
          inQuotes = false;
        } else {
          current += char;
        }
      } else {
        if (char === '"') {
          // Start of quoted field
          inQuotes = true;
        } else if (char === ',') {
          // Field separator
          result.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
    }
    
    // Push last field
    result.push(current.trim());
    
    return result;
  }

  /**
   * Validate invoice data
   * @param {Object} invoice - Invoice object
   * @returns {Object} - Validation result with isValid and errors
   */
  static validateInvoice(invoice) {
    const errors = [];   
    
    if (!invoice.BankCode) {
      errors.push('BankCode is required');
    }
    
    if (!invoice.ExpenseCode) {
      errors.push('ExpenseCode is required');
    }
    
    if (!invoice.InvoiceNumber) {
      errors.push('InvoiceNumber is required');
    }
    
    if (invoice.Amount === null || invoice.Amount === undefined || isNaN(invoice.Amount)) {
      errors.push('Amount is required and must be a valid number');
    }
    
    if (!invoice.InvoiceDate) {
      errors.push('InvoiceDate is required');
    }
    
    if (!invoice.DueDate) {
      errors.push('DueDate is required');
    }
    
    return {
      isValid: errors.length === 0,
      errors: errors
    };
  }

  /**
   * Batch process multiple invoices
   * @param {Array} invoiceIds - Array of invoice IDs
   * @param {Object} credentials - Contains vendorPassword and apiGuid
   * @returns {Promise<Array>} - Array of process results
   */
  static async batchProcessInvoices(invoiceIds, credentials) {
    const results = [];
    
    for (const invoiceId of invoiceIds) {
      try {
        const result = await this.processInvoice(invoiceId, credentials);
        results.push({
          invoiceId,
          success: true,
          data: result.data
        });
      } catch (error) {
        results.push({
          invoiceId,
          success: false,
          error: error.response?.data?.message || error.message
        });
      }
    }
    
    return results;
  }
}

export default VMSInvoiceService;
